import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api/axios';
import { Plus, X, Search } from 'lucide-react';

const PRIORITY_COLORS = { kritik: '#ef4444', yuksek: '#f97316', normal: '#007aff', dusuk: '#9ca3af' };
const PRIORITY_LABELS = { kritik: 'Kritik', yuksek: 'Yüksek', normal: 'Normal', dusuk: 'Düşük' };

export default function AdminKanban() {
  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [search, setSearch] = useState('');
  const [dragTask, setDragTask] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const STATUS_MAP = {
    'Yapılacak': 'yapilacak',
    'Devam Ediyor': 'devam-ediyor',
    'İncelemede': 'incelemede',
    'Tamamlandı': 'tamamlandi',
  };

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterProject) params.append('project_id', filterProject);
    if (search) params.append('search', search);
    api.get('/tasks/tasks?' + params.toString()).then(r => setTasks(r.data));
  }, [filterProject, search]);

  useEffect(() => {
    api.get('/tasks/kanban-columns').then(r => setColumns(r.data));
    api.get('/tasks/projects').then(r => setProjects(r.data));
    load();
  }, [load]);

  const getColumnTasks = (col) => {
    const statusKey = STATUS_MAP[col.name] || col.name.toLowerCase().replace(' ', '-');
    return tasks.filter(t => t.status === statusKey);
  };

  const handleDrop = async (col) => {
    if (!dragTask) return;
    const statusKey = STATUS_MAP[col.name] || col.name.toLowerCase().replace(' ', '-');
    await api.patch('/tasks/tasks/' + dragTask.id + '/status', { status: statusKey });
    setDragTask(null);
    setDragOver(null);
    load();
  };

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e' }}>📋 Kanban Panosu</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '6px 12px', background: 'white' }}>
            <Search size={13} color="#9ca3af" />
            <input placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ border: 'none', outline: 'none', fontSize: 13, width: 120 }} />
          </div>
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
            style={{ padding: '7px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', background: 'white' }}>
            <option value="">Tüm Projeler</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16, alignItems: 'flex-start' }}>
        {columns.map(col => {
          const colTasks = getColumnTasks(col);
          const isDragTarget = dragOver === col.id;
          return (
            <div key={col.id}
              onDragOver={e => { e.preventDefault(); setDragOver(col.id); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={() => handleDrop(col)}
              style={{ minWidth: 280, width: 280, flexShrink: 0, background: isDragTarget ? 'rgba(0,122,255,0.06)' : '#f8fafc', borderRadius: 14, border: isDragTarget ? '2px dashed #007aff' : '2px solid transparent', transition: 'all 0.2s' }}>

              {/* Sütun başlık */}
              <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: col.color }} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{col.name}</span>
                  <span style={{ background: col.color + '20', color: col.color, borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{colTasks.length}</span>
                </div>
              </div>

              {/* Görev kartları */}
              <div style={{ padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 100 }}>
                {colTasks.map(task => (
                  <div key={task.id}
                    draggable
                    onDragStart={() => setDragTask(task)}
                    onDragEnd={() => { setDragTask(null); setDragOver(null); }}
                    style={{ background: 'white', borderRadius: 10, padding: '12px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', cursor: 'grab', border: `1px solid rgba(0,0,0,0.06)`, borderLeft: `3px solid ${PRIORITY_COLORS[task.priority]}`, transition: 'all 0.15s', opacity: dragTask?.id === task.id ? 0.5 : 1 }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'}>
                    {task.project_name && (
                      <div style={{ fontSize: 10, color: task.project_color || '#007aff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{task.project_name}</div>
                    )}
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', lineHeight: 1.4, marginBottom: 8 }}>{task.title}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: PRIORITY_COLORS[task.priority] }}>{PRIORITY_LABELS[task.priority]}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {task.due_date && (
                          <span style={{ fontSize: 10, color: task.is_overdue ? '#ef4444' : '#9ca3af', fontWeight: task.is_overdue ? 700 : 400 }}>
                            {task.is_overdue && '⚠️'} {new Date(task.due_date).toLocaleDateString('tr-TR')}
                          </span>
                        )}
                        {task.assigned_name && (
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
                            {task.assigned_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>
                    {parseInt(task.total_subtasks) > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ height: 3, background: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: (task.progress || 0) + '%', background: '#007aff', borderRadius: 2 }} />
                        </div>
                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{task.completed_subtasks}/{task.total_subtasks} alt görev</div>
                      </div>
                    )}
                  </div>
                ))}

                {colTasks.length === 0 && (
                  <div style={{ padding: 16, textAlign: 'center', color: '#d1d5db', fontSize: 12 }}>
                    {isDragTarget ? '✓ Buraya bırak' : 'Görev yok'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
